import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Notifications.module.scss";
import { CheckCheck, Bell, Heart, MessageCircle, UserPlus, UserMinus, Users, Shield, Trophy, Clock, Swords, Radio, XCircle } from "lucide-react";
import { api } from "../../services/api";
import { useSocket } from "../../hooks/useSocket";
import { useUser } from "../../context/UserContext";
import Avatar from "../../components/Avatar/Avatar";
import EmptyState from "../../components/EmptyState";
import type { Notification } from "../../services/api/NotificationsApi";

type NotificationType = Notification["type"];
type TabKey = "all" | "comments" | NotificationType;

const TABs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "all", label: "All", icon: <Bell size={16} /> },
  { key: "new_follower", label: "Followers", icon: <UserPlus size={16} /> },
  { key: "follow_request", label: "Requests", icon: <Shield size={16} /> },
  { key: "post_like", label: "Likes", icon: <Heart size={16} /> },
  { key: "achievement_unlock", label: "Achievements", icon: <Trophy size={16} /> },
  { key: "new_message", label: "Messages", icon: <MessageCircle size={16} /> },
  { key: "comments", label: "Comments", icon: <MessageCircle size={16} /> },
];

function iconForType(t: NotificationType) {
  switch (t) {
    case "new_follower":
      return <UserPlus size={16} />;
    case "follow_request":
      return <Shield size={16} />;
    case "follow_request_approved":
      return <UserPlus size={16} />;
    case "post_like":
      return <Heart size={16} />;
    case "achievement_unlock":
      return <Trophy size={16} />;
    case "new_message":
      return <MessageCircle size={16} />;
    case "comment":
    case "comment_reply":
      return <MessageCircle size={16} />;
    case "lfg_player_joined":
      return <UserPlus size={16} />;
    case "lfg_player_left":
      return <UserMinus size={16} />;
    case "lfg_group_full":
      return <Users size={16} />;
    case "lfg_expired":
      return <Clock size={16} />;
    case "match_created":
      return <Swords size={16} />;
    case "match_channel_ready":
      return <Radio size={16} />;
    case "match_cancelled":
      return <XCircle size={16} />;
    case "channel_creation_failed":
      return <XCircle size={16} />;
    case "queue_match_found":
      return <Swords size={16} />;
    case "queue_expired":
      return <Clock size={16} />;
    case "lobby_cancelled":
      return <XCircle size={16} />;
    default:
      return <Bell size={16} />;
  }
}

function titleForNotification(n: Notification): string {
  switch (n.type) {
    case "new_follower":
      return n.metadata?.followerUsername
        ? `${n.metadata.followerUsername} followed you`
        : "Someone followed you";
    case "follow_request":
      return n.metadata?.requesterUsername
        ? `${n.metadata.requesterUsername} requested to follow you`
        : "New follow request";
    case "follow_request_approved":
      return n.metadata?.targetUsername
        ? `${n.metadata.targetUsername} approved your follow request`
        : "Your follow request was approved";
    case "post_like":
      return n.metadata?.likerUsername
        ? `${n.metadata.likerUsername} liked your post`
        : "Someone liked your post";
    case "achievement_unlock":
      return n.metadata?.achievementName
        ? `Achievement unlocked: ${n.metadata.achievementName}`
        : "Achievement unlocked";
    case "new_message":
      return n.metadata?.senderUsername
        ? `New message from ${n.metadata.senderUsername}`
        : "New message";
    case "comment":
      return n.metadata?.commenterUsername
        ? `${n.metadata.commenterUsername} commented on your post`
        : "Someone commented on your post";
    case "comment_reply":
      return n.metadata?.replierUsername
        ? `${n.metadata.replierUsername} replied to your comment`
        : "Someone replied to your comment";
    case "lfg_player_joined":
      return n.metadata?.playerUsername
        ? `${n.metadata.playerUsername} joined your group`
        : "A player joined your group";
    case "lfg_player_left":
      return n.metadata?.playerUsername
        ? `${n.metadata.playerUsername} left your group`
        : "A player left your group";
    case "lfg_group_full":
      return n.metadata?.gameName
        ? `Your LFG group for ${n.metadata.gameName} is full`
        : "Your LFG group is full";
    case "lfg_expired":
      return "Your LFG group expired";
    case "match_created":
      return "Match created";
    case "match_channel_ready":
      return "Match channel is ready";
    case "match_cancelled":
      return n.metadata?.reason
        ? `Match was cancelled: ${n.metadata.reason}`
        : "Match was cancelled";
    case "channel_creation_failed":
      return "Channel creation failed";
    case "queue_match_found":
      return n.metadata?.gameName
        ? `Match found for ${n.metadata.gameName}!`
        : "Match found!";
    case "queue_expired":
      return "Queue expired";
    case "lobby_cancelled":
      return "Lobby was cancelled";
    default:
      return "Notification";
  }
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;

  if (diffMs < 60_000) return "Just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h`;
  if (diffMs < 604_800_000) return `${Math.floor(diffMs / 86_400_000)}d`;
  return new Date(dateStr).toLocaleDateString();
}

export function getActorInfo(n: Notification): { username?: string; avatarUrl?: string; userId?: string } | null {
  const m = n.metadata;
  switch (n.type) {
    case "new_follower": return { username: m.followerUsername, avatarUrl: m.followerAvatarUrl, userId: m.followerId };
    case "post_like": return { username: m.likerUsername, avatarUrl: m.likerAvatarUrl, userId: m.likerId };
    case "comment": return { username: m.commenterUsername, avatarUrl: m.commenterAvatarUrl, userId: m.commenterId };
    case "comment_reply": return { username: m.replierUsername, avatarUrl: m.replierAvatarUrl, userId: m.replierId };
    case "new_message": return { username: m.senderUsername, avatarUrl: m.senderAvatarUrl, userId: m.senderId };
    case "follow_request": return { username: m.requesterUsername, avatarUrl: m.requesterAvatarUrl, userId: m.requesterId };
    case "follow_request_approved": return { username: m.targetUsername, avatarUrl: m.targetAvatarUrl, userId: m.targetId };
    case "lfg_player_joined":
    case "lfg_player_left": return { username: m.playerUsername, avatarUrl: m.playerAvatarUrl, userId: m.playerId };
    default: return null;
  }
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [tab, setTab] = useState<TabKey>("all");
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const { subscribe } = useSocket();
  const listRef = useRef<HTMLElement>(null);

  // Fetch initial notifications
  useEffect(() => {
    let cancelled = false;

    async function fetchInitial() {
      setLoading(true);
      try {
        const [listRes, countRes] = await Promise.all([
          api.notifications.list(),
          api.notifications.getUnreadCount(),
        ]);
        if (cancelled) return;
        setItems(listRes.items);
        setCursor(listRes.cursor);
        setHasMore(listRes.hasMore);
        setUnreadCount(countRes.count);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchInitial();
    return () => { cancelled = true; };
  }, []);

  // Subscribe to real-time notification events
  useEffect(() => {
    const unsub = subscribe("notification", (notification: Notification) => {
      setItems((prev) => [notification, ...prev]);
      setUnreadCount((c) => c + 1);
    });
    return unsub;
  }, [subscribe]);

  // Load more (cursor pagination)
  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.notifications.list(cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error("Failed to load more notifications:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore]);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    const prevItems = items;
    const prevUnreadCount = unreadCount;

    // Optimistic update
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await api.notifications.markAllRead();
    } catch (err) {
      // Revert on failure
      setItems(prevItems);
      setUnreadCount(prevUnreadCount);
      console.error("Failed to mark all as read:", err);
    }
  }, [items, unreadCount]);

  // Mark single notification as read and navigate to relevant page
  const openNotification = useCallback(async (notificationId: string) => {
    const notification = items.find((n) => n.notificationId === notificationId);
    if (!notification) return;

    if (!notification.read) {
      setItems((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount((c) => Math.max(0, c - 1));

      try {
        await api.notifications.markRead(notificationId);
      } catch (err) {
        setItems((prev) =>
          prev.map((n) =>
            n.notificationId === notificationId ? { ...n, read: false } : n
          )
        );
        setUnreadCount((c) => c + 1);
        console.error("Failed to mark notification as read:", err);
      }
    }

    // Navigate based on notification type
    switch (notification.type) {
      case "new_message":
        if (notification.metadata?.conversationId) {
          navigate(`/messages?conversation=${notification.metadata.conversationId}`);
        } else {
          navigate("/messages");
        }
        break;
      case "new_follower":
        if (notification.metadata?.followerId) {
          navigate(`/profile?user=${notification.metadata.followerId}`);
        }
        break;
      case "follow_request":
        navigate("/follow-requests");
        break;
      case "follow_request_approved":
        if (notification.metadata?.targetId) {
          navigate(`/profile?user=${notification.metadata.targetId}`);
        }
        break;
      case "post_like":
        if (notification.metadata?.postId) {
          navigate(`/post/${notification.metadata.postId}`);
        }
        break;
      case "achievement_unlock":
        if (user) {
          navigate(`/profile?user=${user.userId}`);
        } else {
          navigate("/profile");
        }
        break;
      case "comment":
      case "comment_reply":
        if (notification.metadata?.postId) {
          navigate(`/post/${notification.metadata.postId}`);
        }
        break;
      case "lfg_player_joined":
      case "lfg_player_left":
      case "lfg_group_full":
      case "lfg_expired":
      case "match_created":
      case "match_channel_ready":
      case "match_cancelled":
      case "channel_creation_failed":
      case "queue_match_found":
      case "queue_expired":
      case "lobby_cancelled":
        break;
    }
  }, [items, navigate]);

  // Filter by tab
  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "comments") return items.filter((n) => n.type === "comment" || n.type === "comment_reply");
    return items.filter((n) => n.type === tab);
  }, [items, tab]);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>Notifications</h1>
            <p className={styles.subtitle}>
              {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
            </p>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={markAllRead}
              disabled={unreadCount === 0}
              title="Mark all as read"
            >
              <CheckCheck size={16} />
              Mark all
            </button>
          </div>
        </header>

        <div className={styles.toolbar}>
          <div className={styles.tabs} role="tablist" aria-label="Notification filters">
            {TABs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
                onClick={() => setTab(t.key)}
                role="tab"
                aria-selected={tab === t.key}
              >
                <span className={styles.tabIcon}>{t.icon}</span>
                <span className={styles.tabLabel}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <section className={styles.list} ref={listRef} aria-label="Notification list">
          {loading ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <Bell size={18} />
              </div>
              <div className={styles.emptyTitle}>Loading…</div>
            </div>
          ) : filtered.length === 0 ? (
            items.length === 0 ? (
              <EmptyState
                icon={<Bell size={18} />}
                heading="No notifications yet"
                description="When something happens, you'll see it here."
              />
            ) : (
              <EmptyState
                icon={TABs.find((t) => t.key === tab)?.icon || <Bell size={18} />}
                heading="Nothing here"
                description="You don't have notifications in this category yet."
              />
            )
          ) : (
            <>
              {filtered.map((n) => (
                n.type === "follow_request" ? (
                  <div
                    key={n.notificationId}
                    className={`${styles.item} ${!n.read ? styles.itemUnread : ""}`}
                  >
                    <div className={styles.left} onClick={() => n.metadata?.requesterId && navigate(`/profile?user=${n.metadata.requesterId}`)} style={{ cursor: "pointer" }}>
                      <div className={styles.avatar}>
                        <Avatar
                          src={n.metadata?.requesterAvatarUrl}
                          username={n.metadata?.requesterUsername || "?"}
                          size="md"
                          onClick={() => n.metadata?.requesterId && navigate(`/profile?user=${n.metadata.requesterId}`)}
                        />
                      </div>
                    </div>

                    <div className={styles.body}>
                      <div className={styles.rowTop}>
                        <div className={styles.itemTitle}>
                          <span className={styles.username}>{n.metadata?.requesterUsername || "Someone"}</span>
                          {" "}requested to follow you · {timeAgo(n.createdAt)}
                        </div>
                      </div>
                      <div className={styles.requestActions}>
                        <button
                          type="button"
                          className={styles.acceptBtn}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              if (n.metadata?.requestId) {
                                await api.social.approveFollowRequest(n.metadata.requestId);
                              }
                            } catch (err) {
                              // Follow request may already be gone — that's fine
                            }
                            // Delete the notification from DB so it doesn't come back on refresh
                            try {
                              await api.notifications.deleteNotification(n.notificationId);
                            } catch (err) {
                              // Ignore — best effort
                            }
                            setItems((prev) => prev.filter((item) => item.notificationId !== n.notificationId));
                            if (!n.read) {
                              setUnreadCount((c) => Math.max(0, c - 1));
                            }
                          }}
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          className={styles.rejectBtn}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              if (n.metadata?.requestId) {
                                await api.social.rejectFollowRequest(n.metadata.requestId);
                              }
                            } catch (err) {
                              // Follow request may already be gone — that's fine
                            }
                            // Delete the notification from DB so it doesn't come back on refresh
                            try {
                              await api.notifications.deleteNotification(n.notificationId);
                            } catch (err) {
                              // Ignore — best effort
                            }
                            setItems((prev) => prev.filter((item) => item.notificationId !== n.notificationId));
                            if (!n.read) {
                              setUnreadCount((c) => Math.max(0, c - 1));
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                <button
                  type="button"
                  key={n.notificationId}
                  className={`${styles.item} ${!n.read ? styles.itemUnread : ""}`}
                  onClick={() => openNotification(n.notificationId)}
                >
                  <div className={styles.left}>
                    <div className={styles.avatar}>
                      {(() => {
                        const actorInfo = getActorInfo(n);
                        if (actorInfo) {
                          return (
                            <Avatar
                              src={actorInfo.avatarUrl}
                              username={actorInfo.username || "?"}
                              size="md"
                              onClick={() => actorInfo.userId && navigate(`/profile?user=${actorInfo.userId}`)}
                            />
                          );
                        }
                        return (
                          <div className={styles.avatarFallback} aria-hidden="true">
                            {iconForType(n.type)}
                          </div>
                        );
                      })()}
                    </div>

                    <div className={styles.typeIcon} aria-hidden="true">
                      {iconForType(n.type)}
                    </div>
                  </div>

                  <div className={styles.body}>
                    <div className={styles.rowTop}>
                      <div className={styles.itemTitle}>{titleForNotification(n)}</div>
                      <div className={styles.time}>{timeAgo(n.createdAt)}</div>
                    </div>
                  </div>

                  {!n.read && <div className={styles.unreadDot} aria-hidden="true" />}
                </button>
                )
              ))}

              {hasMore && (
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{ alignSelf: "center", marginTop: "8px" }}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
